/**
 * AI Agent Endpoint List Page - Table view of all AI agent endpoints.
 */
import { useEffect, useState, useCallback, useMemo, type ChangeEvent, type ReactElement, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  AltRoute,
  Cloud,
  Computer,
  Star,
  Clear,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { RequirePermission } from '../contexts/PermissionContext';
import api from '../services/api';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { useListSearchParams } from '../hooks/useListSearchParams';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';
import FilteredListLayout from '../components/FilteredListLayout';
import ListActionButton from '../components/ListActionButton';
import type { AIAgentEndpoint, AIAgentProvider, Agent } from '../types';
import usePageTitle from '../hooks/usePageTitle';

const providerTypeConfig: Record<
  string,
  {
    color: 'default' | 'primary' | 'secondary';
    icon: ReactNode;
  }
> = {
  cloud: { color: 'primary', icon: <Cloud sx={{ fontSize: 16 }} /> },
  local: { color: 'secondary', icon: <Computer sx={{ fontSize: 16 }} /> },
};

export default function AiAgentEndpointListPage() {
  const { t, i18n } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:aiAgentEndpoints.title'));
  const columnTitle = (label?: string) => (label ?? '').toLocaleUpperCase(i18n.language);
  const [endpoints, setEndpoints] = useState<AIAgentEndpoint[]>([]);
  const [providers, setProviders] = useState<AIAgentProvider[]>([]);
  const [aiAgents, setAiAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalEndpoints, setTotalEndpoints] = useState(0);
  const { confirm, DialogComponent } = useConfirmDialog();

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['status', 'ai_agent_provider_id', 'ai_agent_id', 'search'],
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: '-created_at',
  });

  const statusFilterValue = filters.status ?? '';
  const providerFilterValue = filters.ai_agent_provider_id ?? '';
  const aiAgentFilterValue = filters.ai_agent_id ?? '';
  const searchFilterValue = filters.search ?? '';
  const orderingParam = ordering || '-created_at';

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const tableRowHoverBg = alpha(theme.palette.primary.main, 0.08);
  const flatButtonSx = {
    textTransform: 'none',
    boxShadow: 'none',
    backgroundImage: 'none',
  };
  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiSelect-select': {
      padding: '11px 12px',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
  };

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: t('common:status.active') },
      { value: 'inactive', label: t('common:status.inactive') },
    ],
    [t],
  );

  const providerOptions = useMemo(
    () => providers.map((provider) => ({ value: String(provider.id), label: provider.name })),
    [providers],
  );

  const aiAgentOptions = useMemo(() => {
    const filteredAgents = providerFilterValue
      ? aiAgents.filter((aiAgent) => String(aiAgent.aiAgentProviderId || '') === providerFilterValue)
      : aiAgents;
    return filteredAgents.map((aiAgent) => ({ value: String(aiAgent.id), label: aiAgent.name }));
  }, [aiAgents, providerFilterValue]);

  const fetchBootstrapData = useCallback(async () => {
    try {
      const [providersResponse, aiAgentsResponse] = await Promise.all([
        api.get('/api/ai-agents-providers/available/'),
        api.get('/api/agents/list_simple/'),
      ]);
      const providerData = providersResponse.data.results ?? providersResponse.data ?? [];
      setProviders(
        providerData.filter((provider: AIAgentProvider) => ['cloud', 'local'].includes(provider.providerType)),
      );

      const aiAgentData = aiAgentsResponse.data.results ?? aiAgentsResponse.data ?? [];
      setAiAgents(Array.isArray(aiAgentData) ? aiAgentData : []);
    } catch (err) {
      console.error('Error loading AI agent endpoint filters:', err);
      setProviders([]);
      setAiAgents([]);
    }
  }, []);

  const fetchEndpoints = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);
      const params: Record<string, string | number | boolean> = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: orderingParam,
      };

      if (statusFilterValue === 'active') {
        params.is_active = true;
      } else if (statusFilterValue === 'inactive') {
        params.is_active = false;
      }

      if (providerFilterValue) {
        params.ai_agent_provider_id = providerFilterValue;
      }

      if (aiAgentFilterValue) {
        params.ai_agent_id = aiAgentFilterValue;
      }

      if (searchFilterValue) {
        params.search = searchFilterValue;
      }

      const response = await api.get('/api/ai-agent-endpoints/', { params });
      const data = response.data.results ?? response.data;
      setEndpoints(Array.isArray(data) ? data : []);
      setTotalEndpoints(response.data.count ?? data.length ?? 0);
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { detail?: string } } };
      console.error('Error loading AI agent endpoints:', err);
      setError(apiError?.response?.data?.detail || t('pages:aiAgentEndpoints.errors.loading'));
      setEndpoints([]);
    } finally {
      setLoading(false);
    }
  }, [
    aiAgentFilterValue,
    orderingParam,
    page,
    providerFilterValue,
    rowsPerPage,
    searchFilterValue,
    statusFilterValue,
    t,
  ]);

  useEffect(() => {
    fetchBootstrapData();
  }, [fetchBootstrapData]);

  useEffect(() => {
    fetchEndpoints();
  }, [fetchEndpoints]);

  const handleDelete = async (endpoint: AIAgentEndpoint) => {
    const confirmed = await confirm({
      title: t('pages:aiAgentEndpoints.deleteTitle'),
      message: t('pages:aiAgentEndpoints.deleteMessage', { name: endpoint.name }),
      type: 'warning',
      icon: <Delete color="error" />,
      confirmIcon: <Delete />,
      confirmText: t('common:actions.delete'),
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/ai-agent-endpoints/${endpoint.id}/`);
      fetchEndpoints();
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } } };
      setError(apiError?.response?.data?.error || t('pages:aiAgentEndpoints.errors.deleting'));
    }
  };

  const filterControls = (
    <>
      <TextField
        size="small"
        label={t('common:actions.search')}
        placeholder={t('pages:aiAgentEndpoints.filters.searchPlaceholder')}
        value={searchFilterValue}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const value = event.target.value;
          setParams({ search: value || undefined }, { resetPage: true });
        }}
        sx={{ ...filterInputSx, minWidth: 220, flex: '1 1 220px' }}
      />
      <FormControl size="small" sx={{ minWidth: 220, ...filterInputSx }}>
        <InputLabel id="ai-agent-endpoints-provider-filter-label">
          {t('pages:aiAgentEndpoints.filters.providerLabel')}
        </InputLabel>
        <Select
          labelId="ai-agent-endpoints-provider-filter-label"
          value={providerFilterValue}
          label={t('pages:aiAgentEndpoints.filters.providerLabel')}
          onChange={(event: SelectChangeEvent<string>) => {
            const value = event.target.value;
            setParams(
              { ai_agent_provider_id: value || undefined, ai_agent_id: undefined },
              { resetPage: true },
            );
          }}
        >
          <MenuItem value="">
            <em>{t('common:common.all')}</em>
          </MenuItem>
          {providerOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 220, ...filterInputSx }}>
        <InputLabel id="ai-agent-endpoints-agent-filter-label">
          {t('pages:aiAgentEndpoints.filters.agentLabel')}
        </InputLabel>
        <Select
          labelId="ai-agent-endpoints-agent-filter-label"
          value={aiAgentFilterValue}
          label={t('pages:aiAgentEndpoints.filters.agentLabel')}
          onChange={(event: SelectChangeEvent<string>) => {
            const value = event.target.value;
            setParams({ ai_agent_id: value || undefined }, { resetPage: true });
          }}
        >
          <MenuItem value="">
            <em>{t('common:common.all')}</em>
          </MenuItem>
          {aiAgentOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 160, ...filterInputSx }}>
        <InputLabel id="ai-agent-endpoints-status-filter-label">
          {t('pages:aiAgentEndpoints.filters.statusLabel')}
        </InputLabel>
        <Select
          labelId="ai-agent-endpoints-status-filter-label"
          value={statusFilterValue}
          label={t('pages:aiAgentEndpoints.filters.statusLabel')}
          onChange={(event: SelectChangeEvent<string>) => {
            const value = event.target.value;
            setParams({ status: value || undefined }, { resetPage: true });
          }}
        >
          <MenuItem value="">
            <em>{t('common:common.all')}</em>
          </MenuItem>
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Clear />}
        disabled={!searchFilterValue && !providerFilterValue && !aiAgentFilterValue && !statusFilterValue}
        onClick={() => {
          if (!searchFilterValue && !providerFilterValue && !aiAgentFilterValue && !statusFilterValue) return;
          setParams(
            {
              search: null,
              ai_agent_provider_id: null,
              ai_agent_id: null,
              status: null,
            },
            { resetPage: true },
          );
        }}
        sx={{
          ...flatButtonSx,
          color: theme.palette.primary.main,
          borderColor: alpha(theme.palette.primary.main, 0.6),
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        {t('common:actions.clear')}
      </Button>
    </>
  );

  const table = (
    <Table>
      <TableHead>
        <TableRow
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '& th': {
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.75rem',
            },
          }}
        >
          <TableCell>{columnTitle(t('pages:aiAgentEndpoints.name'))}</TableCell>
          <TableCell>{columnTitle(t('pages:aiAgentEndpoints.aiAgent'))}</TableCell>
          <TableCell>{columnTitle(t('pages:aiAgentEndpoints.identifier'))}</TableCell>
          <TableCell>{columnTitle(t('pages:aiAgentEndpoints.provider'))}</TableCell>
          <TableCell>{columnTitle(t('pages:aiAgentEndpoints.sipTrunkPool'))}</TableCell>
          <TableCell align="center">{columnTitle(t('pages:aiAgentEndpoints.status'))}</TableCell>
          <TableCell align="center">{columnTitle(t('common:actions.actions'))}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          [...Array(5)].map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {[...Array(7)].map((_, cellIndex) => (
                <TableCell key={cellIndex}>
                  <Skeleton />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : endpoints.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
              <AltRoute sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t('pages:aiAgentEndpoints.noEndpoints')}
              </Typography>
              <RequirePermission permission="ai_agent_endpoints_create">
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  component={RouterLink}
                  to="/ai-agent-endpoints/new"
                  sx={{
                    ...flatButtonSx,
                    color: theme.palette.primary.main,
                    borderColor: alpha(theme.palette.primary.main, 0.6),
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  {t('pages:aiAgentEndpoints.createFirst')}
                </Button>
              </RequirePermission>
            </TableCell>
          </TableRow>
        ) : (
          endpoints.map((endpoint) => {
            const typeConfig = providerTypeConfig[endpoint.aiAgentProviderType] || {
              color: 'default',
              icon: <AltRoute sx={{ fontSize: 16 }} />,
            };

            return (
              <TableRow
                key={endpoint.id}
                hover
                sx={{
                  transition: 'background 0.2s',
                  '&:hover': { backgroundColor: tableRowHoverBg },
                }}
              >
                <LinkTableCell to={`/ai-agent-endpoints/${endpoint.id}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          fontWeight={600}
                          sx={{ '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                        >
                          {endpoint.name}
                        </Typography>
                        {endpoint.isPrimary && (
                          <Tooltip title={t('pages:aiAgentEndpoints.primaryEndpoint')}>
                            <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {endpoint.endpointTypeDisplay}
                      </Typography>
                    </Box>
                  </Box>
                </LinkTableCell>
                <LinkTableCell to={`/ai-agent-endpoints/${endpoint.id}`}>
                  <Typography variant="body2">{endpoint.aiAgentName}</Typography>
                </LinkTableCell>
                <LinkTableCell to={`/ai-agent-endpoints/${endpoint.id}`}>
                  <Typography variant="body2" fontFamily="monospace">
                    {endpoint.identifier}
                  </Typography>
                </LinkTableCell>
                <LinkTableCell to={`/ai-agent-endpoints/${endpoint.id}`}>
                  <Chip
                    icon={typeConfig.icon as ReactElement}
                    label={endpoint.aiAgentProviderName}
                    color={typeConfig.color}
                    size="small"
                    variant="outlined"
                  />
                </LinkTableCell>
                <LinkTableCell to={`/ai-agent-endpoints/${endpoint.id}`}>
                  <Typography variant="body2" color={endpoint.sipTrunkPoolName ? 'text.primary' : 'text.secondary'}>
                    {endpoint.sipTrunkPoolName || t('pages:aiAgentEndpoints.noSipTrunkPool')}
                  </Typography>
                </LinkTableCell>
                <TableCell align="center">
                  <Chip
                    label={endpoint.isActive ? t('common:status.active') : t('common:status.inactive')}
                    color={endpoint.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <RequirePermission permission="ai_agent_endpoints_update">
                      <Tooltip title={t('common:actions.edit')}>
                        <IconButton
                          size="small"
                          component={RouterLink}
                          to={`/ai-agent-endpoints/${endpoint.id}`}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </RequirePermission>
                    <RequirePermission permission="ai_agent_endpoints_delete">
                      <Tooltip title={t('common:actions.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(endpoint)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </RequirePermission>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );

  const tableFooter =
    totalEndpoints > 0 ? (
      <LinkPagination
        component="div"
        count={totalEndpoints}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
        labelRowsPerPage={t('common:pagination.rowsPerPage')}
        getItemAriaLabel={(type) => {
          if (type === 'first') return t('common:pagination.firstPage');
          if (type === 'last') return t('common:pagination.lastPage');
          if (type === 'next') return t('common:pagination.nextPage');
          return t('common:pagination.previousPage');
        }}
        getPageLink={getPageLink}
      />
    ) : undefined;

  return (
    <Box>
      <FilteredListLayout
        headerProps={{
          icon: <AltRoute sx={{ fontSize: 32, color: 'primary.main' }} />,
          title: t('pages:aiAgentEndpoints.title'),
          subtitle: t('pages:aiAgentEndpoints.subtitle'),
          actions: (
            <>
              <ListActionButton variant="outlined" startIcon={<Refresh />} onClick={fetchEndpoints}>
                {t('common:actions.refresh')}
              </ListActionButton>
              <RequirePermission permission="ai_agent_endpoints_create">
                <ListActionButton
                  variant="contained"
                  startIcon={<Add />}
                  component={RouterLink}
                  to="/ai-agent-endpoints/new"
                >
                  {t('pages:aiAgentEndpoints.new')}
                </ListActionButton>
              </RequirePermission>
            </>
          ),
        }}
        filterControls={filterControls}
        table={table}
        tableFooter={tableFooter}
        errorContent={
          error && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'error.main', borderRadius: 1 }}>
              <Typography color="error.contrastText">{error}</Typography>
            </Box>
          )
        }
      />

      {DialogComponent}
    </Box>
  );
}
